import { PRESCRIPTION_STYLES, URDU_FONT_CSS, delay, escapeHtml, whenReady } from './printDocument';
import { paginatePrintSheet } from './paginatePrintSheet';

// Saving a printed sheet as a PDF.
//
// Every Print button writes a sheet into a clean window and prints it. Download PDF takes the same
// sheet, lays it out in a frame kept off screen (so its styles never touch the app), photographs it
// with html2canvas and lays the photographs on pages with jsPDF. Both libraries load on the first
// PDF only, so they add nothing to opening the app.
//
// The page is a picture, so its text cannot be selected or searched. What it buys is a PDF that
// looks exactly like the printout, Urdu included, with no font to embed.

const PX_PER_MM = 96 / 25.4;
const SCALE = 2;
const A4 = { width: 210, height: 297 };
/** The A4 page's margins in mm. "Page 1 of 2" sits in the bottom one. */
const MARGIN = { top: 16, side: 14, bottom: 16 };
const SLIP_WIDTH_MM = 80;
// html2canvas draws onto one canvas, and a browser refuses one much taller than 32,000 pixels, so a
// long report is photographed a few pages at a time: up to 6,000 CSS pixels, 12,000 on the canvas.
const MAX_SHOT_PX = 6000;
// A picture or a font that never arrives must not hold the download up for ever.
const READY_CEILING_MS = 5000;

// Elements kept whole, whatever is in them: a table row, a picture, a drawing, a rule.
const ATOMIC = new Set(['TR', 'IMG', 'svg', 'CANVAS', 'HR', 'INPUT', 'SELECT', 'TEXTAREA', 'BUTTON', 'VIDEO', 'IFRAME']);

// html2canvas finds where text sits on its line by measuring a word beside a 1-pixel picture set
// inline, and it does that in the APP's document, not the sheet's. Tailwind's base styles make every
// picture a block, which threw the measurement out and drew all the text several pixels low: through
// the rule under each table row, and through a row at the foot of a page. This puts that one picture
// back inline while a PDF is made; html2canvas's measuring box is the only hidden box it appends to
// the body.
const METRICS_FIX = 'body > div[style*="visibility: hidden"] > img { display: inline !important; }';

// The frame never shows a scrollbar. A sheet longer than the frame would otherwise get one, laid out
// that much narrower and so wrapped differently from the copy html2canvas draws (which is as tall as
// the sheet, so has none): every cut measured on the first fell a little further off the second on
// each page.
const FRAME_BASE = 'html { overflow: hidden !important; } html, body { margin: 0; padding: 0; background: #fff; }';

const ownText = (el) => Array.from(el.childNodes).some((n) => n.nodeType === 3 && n.nodeValue.trim());

/**
 * Where the sheet may be cut, in CSS pixels down the document. Anything holding text of its own, and
 * anything in ATOMIC, is kept whole, so a cut only runs through a gap where none of them is: under
 * a table row, between paragraphs, never through a line of text. `forced` are the page breaks the
 * sheet asks for itself, such as the start of each topic of a guide.
 */
export function breakPoints(body) {
  const win = body.ownerDocument.defaultView;
  // From the top of the document, which is where html2canvas draws from, and not from the body's: a
  // first heading's top margin collapses through the body and starts it lower down the page, and
  // every cut came out that much too high, through the foot of a box.
  const origin = body.ownerDocument.documentElement.getBoundingClientRect().top;
  const boxes = [];
  const forced = [];
  body.querySelectorAll('*').forEach((el) => {
    // The parts of a drawing are kept whole with the drawing.
    if (el.tagName !== 'svg' && el instanceof win.SVGElement) return;
    const style = win.getComputedStyle(el);
    if (style.display === 'none') return;
    const r = el.getBoundingClientRect();
    if (style.breakAfter === 'page' || style.pageBreakAfter === 'always') forced.push(r.bottom - origin);
    if (style.breakBefore === 'page' || style.pageBreakBefore === 'always') forced.push(r.top - origin);
    if (r.height > 0 && (ATOMIC.has(el.tagName) || ownText(el))) boxes.push([r.top - origin, r.bottom - origin]);
  });
  boxes.sort((a, b) => a[0] - b[0]);
  // Overlapping boxes make one block. A page may end where the next block begins.
  const breaks = [];
  let bottom = boxes.length ? boxes[0][1] : 0;
  boxes.slice(1).forEach(([t, b]) => {
    if (t < bottom - 0.5) bottom = Math.max(bottom, b);
    else {
      breaks.push(t);
      bottom = b;
    }
  });
  return { breaks, forced };
}

/**
 * Each page's [top, bottom] in CSS pixels down the sheet. A page ends at the lowest break that fits
 * on it, or at a break the sheet forces before that. Where nothing fits it is cut at the page, and it
 * is also cut there rather than ending almost empty above something that will not fit on any page.
 */
export const pageSlices = (breaks, total, pageHeight, forced = []) => {
  const slices = [];
  let start = 0;
  while (start < total - 1) {
    const limit = start + pageHeight;
    const hard = forced.filter((b) => b > start + 1 && b < total - 1 && b <= limit);
    let end;
    if (hard.length) end = Math.min(...hard);
    else if (limit >= total) end = total;
    else {
      const fits = breaks.filter((b) => b > start && b <= limit);
      end = fits.length ? Math.max(...fits) : limit;
      const next = Math.min(total, ...breaks.filter((b) => b > limit));
      if (end - start < pageHeight * 0.2 && next - end > pageHeight) end = limit;
    }
    slices.push([start, end]);
    start = end;
  }
  return slices;
};

/** Consecutive slices grouped so that no one photograph is taller than MAX_SHOT_PX. */
export const shotsFor = (slices) => {
  const shots = [];
  slices.forEach((s) => {
    const last = shots[shots.length - 1];
    if (last && s[1] - last.from <= MAX_SHOT_PX) {
      last.to = s[1];
      last.slices.push(s);
    } else shots.push({ from: s[0], to: s[1], slices: [s] });
  });
  return shots;
};

/** "INV-1/2: x" made safe to save under any system's rules, with ".pdf" on the end. */
export const pdfFileName = (name) => {
  const base = String(name || 'Document').replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim() || 'Document';
  return /\.pdf$/i.test(base) ? base : `${base}.pdf`;
};

const jpeg = (canvas) => canvas.toDataURL('image/jpeg', 0.92);

/** One strip of `canvas`, `from` to `to` in CSS pixels down `shotFrom`, as its own canvas. */
const piece = (canvas, shotFrom, from, to) => {
  const out = document.createElement('canvas');
  out.width = canvas.width;
  out.height = Math.max(1, Math.round((to - from) * SCALE));
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(canvas, 0, Math.round((from - shotFrom) * SCALE), canvas.width, out.height, 0, 0, out.width, out.height);
  return out;
};

/**
 * Lays `bodyHtml` out under `styles` as the printed sheet would be, and saves it as a PDF in the
 * browser's downloads.
 *
 * @param {object}  opts
 * @param {string}  opts.fileName   what the download is called; ".pdf" is added when missing
 * @param {string}  [opts.title]    the PDF's own title
 * @param {string}  [opts.styles]   the sheet's CSS, as handed to printHtml
 * @param {string}  opts.bodyHtml   the sheet's markup, as handed to printHtml
 * @param {string}  [opts.headHtml] more for the frame's head, such as the app's own stylesheets
 * @param {'A4'|'SLIP'} [opts.paper] A4 pages with margins, or one 80 mm strip as long as the slip
 * @param {number}  [opts.widthPx]  lay the sheet out this wide and shrink it onto the page. By default
 *   it is laid out at exactly the page's printable width, as it prints.
 * @param {boolean} [opts.wholeSheets] the sheet is already cut into A4 sheets carrying their own
 *   margins (the prescription, by `prepare`): each element in the body is one page, edge to edge
 * @param {(doc: Document) => void} [opts.prepare] run once the frame's fonts and pictures are in,
 *   before anything is measured
 */
export async function savePdf({
  fileName, title, styles = '', bodyHtml = '', headHtml = '', paper = 'A4', widthPx, wholeSheets = false, prepare,
} = {}) {
  const [{ jsPDF }, { default: html2canvas }] = await Promise.all([import('jspdf'), import('html2canvas')]);
  const slip = paper === 'SLIP';
  const pageWidthMm = slip ? SLIP_WIDTH_MM : wholeSheets ? A4.width : A4.width - 2 * MARGIN.side;
  const width = widthPx || Math.round(pageWidthMm * PX_PER_MM);

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  Object.assign(frame.style, { position: 'fixed', left: '-10000px', top: '0', width: `${width}px`, height: '1200px', border: '0' });
  document.body.appendChild(frame);
  const metricsFix = document.createElement('style');
  metricsFix.textContent = METRICS_FIX;
  document.head.appendChild(metricsFix);
  try {
    const doc = frame.contentDocument;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${escapeHtml(title || fileName || '')}</title>
<link rel="stylesheet" href="${URDU_FONT_CSS}" />${headHtml}
<style>${FRAME_BASE}</style><style>${styles}</style></head><body>${bodyHtml}</body></html>`);
    doc.close();
    await Promise.race([whenReady(frame.contentWindow), delay(READY_CEILING_MS)]);
    if (prepare) prepare(doc);

    const body = doc.body;
    // A sheet that sets its own width (a slip is 80 mm plus its padding) may be wider than the frame.
    const fullWidth = Math.max(width, Math.ceil(doc.documentElement.scrollWidth));
    frame.style.width = `${fullWidth}px`;
    // The sheet's own height, down from the top of the document. The document's scroll height would
    // be at least the frame's, and a short slip would come out as long as the frame with the rest of
    // it blank.
    const docTop = doc.documentElement.getBoundingClientRect().top;
    const total = Math.ceil(Math.max(
      body.scrollHeight,
      body.getBoundingClientRect().bottom - docTop,
      ...Array.from(body.children).map((el) => el.getBoundingClientRect().bottom - docTop),
    ));
    frame.style.height = `${total}px`;
    const shoot = (el, opts = {}) => html2canvas(el, {
      scale: SCALE, backgroundColor: '#ffffff', logging: false,
      windowWidth: fullWidth, windowHeight: total, scrollX: 0, scrollY: 0, ...opts,
    });

    let pdf;
    if (slip) {
      // One strip, as long as the slip, at its natural size.
      const mm = (px) => px / PX_PER_MM;
      pdf = new jsPDF({ unit: 'mm', format: [mm(fullWidth), mm(total)], orientation: 'portrait' });
      pdf.addImage(jpeg(await shoot(body)), 'JPEG', 0, 0, mm(fullWidth), mm(total));
    } else if (wholeSheets) {
      pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const mmPerPx = A4.width / fullWidth;
      const sheetPx = A4.height / mmPerPx;
      const sheets = Array.from(body.children).filter((el) => el.getBoundingClientRect().height > 0);
      let first = true;
      for (const sheet of sheets) {
        const canvas = await shoot(sheet);
        const height = canvas.height / SCALE;
        // A sheet that did not cut itself up is cut at the page, so nothing is lost off the bottom.
        for (let from = 0; from < height - 1; from += sheetPx) {
          const to = Math.min(height, from + sheetPx);
          if (!first) pdf.addPage();
          first = false;
          pdf.addImage(jpeg(piece(canvas, 0, from, to)), 'JPEG', 0, 0, A4.width, (to - from) * mmPerPx);
        }
      }
    } else {
      pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const mmPerPx = pageWidthMm / fullWidth;
      const { breaks, forced } = breakPoints(body);
      const slices = pageSlices(breaks, total, (A4.height - MARGIN.top - MARGIN.bottom) / mmPerPx, forced);
      let page = 0;
      for (const shot of shotsFor(slices)) {
        const canvas = await shoot(body, { x: 0, y: shot.from, width: fullWidth, height: Math.ceil(shot.to - shot.from) });
        shot.slices.forEach(([from, to]) => {
          if (page > 0) pdf.addPage();
          page += 1;
          pdf.addImage(jpeg(piece(canvas, shot.from, from, to)), 'JPEG', MARGIN.side, MARGIN.top, pageWidthMm, (to - from) * mmPerPx);
          // "Page 1 of 2" at the foot, as the printed sheet has it.
          pdf.setFontSize(8);
          pdf.setTextColor(107, 114, 128);
          pdf.text(`Page ${page} of ${slices.length}`, A4.width - MARGIN.side, A4.height - 8, { align: 'right' });
        });
      }
    }
    pdf.setProperties({ title: title || fileName || '' });
    pdf.save(pdfFileName(fileName || title));
    return true;
  } finally {
    frame.remove();
    metricsFix.remove();
  }
}

/**
 * The app's own stylesheets, for a sheet that is a copy of something on screen rather than a sheet
 * written for paper. Read when asked for, so a stylesheet added since the app opened is included.
 */
export const appStylesheets = () =>
  Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).map((n) => n.outerHTML).join('\n');

/**
 * The prescription `node` as printNode prints it: cut into A4 sheets that each carry the letterhead,
 * the patient bar and the footer, one sheet to a page.
 */
export const savePrescriptionPdf = (node, { title, fileName } = {}) =>
  savePdf({
    title,
    fileName,
    styles: PRESCRIPTION_STYLES,
    bodyHtml: node.outerHTML,
    wholeSheets: true,
    prepare: (doc) => {
      const sheet = doc.querySelector('.prescription-print');
      if (sheet) paginatePrintSheet(sheet);
    },
  });
